import React, { useState, useCallback, useRef, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { getPublicationCountEntities } from "../../../../state/selectors/Publications.selectors";
import HowToArticleLink from "../HowToArticleLink";
import StyledComponentPanel from "../../../atoms/ComponentPanel/StyledComponentPanel";
import SourceEntity from "../../models/SourceEntity";
import PublicationSearchResults from "../PublicationSearchResults";
import MasterMind from "../MasterMind";
import PubmedForm from "../../../PubmedUserEntries/PubmedForm";
import ImperativeTooltip from "../../../atoms/Tooltip/ImperativeTooltip";
import CogIcon from "../../../icons/CogIcon";
import { labels, initialConf } from "../../constants/displayConf";
import ls from "../../../../common/helpers/localStorageActions";
import isPortal from "../../../../common/helpers/isPortal";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import EntitySelector from "./EntitySelector";

function MainPublications({
  entities: ents,
  portalEntities,
  isClinicalResults,
}) {
  const isClinical = isPortal();
  const entities = isClinicalResults ? portalEntities : ents;
  const [entity, selectEntity] = useState(null);
  const [displayOptions, setDisplayOptions] = useState(initialConf);
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const optionsRef = useRef(null);

  const onUpdateConf = (c) => {
    ls.setPublicationsShowTags(c.shouldShowTags);
    ls.setPublicationsShowContr(c.shouldShowContributions);
    ls.setPublicationsShowAbstract(c.shouldShowAbstract);
    setDisplayOptions({ ...c });
  };

  const closeOptions = useCallback(() => setIsOptionsVisible(false), []);

  useEffect(() => {
    const ent = entities?.length ? entities[0] : null;
    selectEntity(ent);
  }, [entities]);

  return (
    <StyledComponentPanel
      title={
        <div className="tw-flex">
          <div className="tw-font-bold tw-flex-grow">Publications</div>
          <div className="tw-absolute tw-top-2 tw-right-2 tw-text-2xs tw-cursor-pointer tw-scale-50 tw-flex tw-gap-x-2">
            <div className="tw-h-8 tw-flex tw-items-center">
              <HowToArticleLink />
            </div>
            <div className="tw-text-3xs">
              {isClinical || entity?.type !== "variant" ? null : <PubmedForm />}
            </div>
          </div>
        </div>
      }
    >
      <div className="tw-m-4 tw-relative">
        <div className="tw-flex tw-items-start tw--mb-4">
          <EntitySelector
            entities={entities}
            selectedEntity={entity}
            selectEntity={selectEntity}
          />
          <div className="tw-flex-grow" />
          <div
            onClick={() => setIsOptionsVisible(true)}
            className="hover:tw-underline tw--mt-3 tw-flex tw-items-center tw-cursor-pointer tw-whitespace-no-wrap tw-font-bold tw-text-xs tw-text-primary"
            ref={optionsRef}
          >
            <CogIcon
              size={16}
              className="tw-fill-current tw-w-4 tw-h-4 tw-ml-1 tw-align-middle"
            />
            Display Options
          </div>
        </div>

        <div className="tw-flex-grow" />

        {isOptionsVisible ? (
          <ImperativeTooltip
            onClose={closeOptions}
            anchor={optionsRef.current}
            shouldFollowObjectOnScroll={false}
            contentClassName="tw-text-blueGray-800"
            ContentRenderer={() => (
              <div className="tw-text-base">
                {Object.keys(displayOptions).map((key) =>
                  key === "shouldShowContributions" && isClinical ? null : (
                    <div key={key}>
                      <div className="tw-flex tw-items-center tw-text-xs tw-py-1">
                        <label className="tw-flex tw-items-center tw-m-0 tw-pl-1 tw-cursor-pointer tw-align-middle">
                          <input
                            type="checkbox"
                            className="tw-align-middle tw-pt-1 tw-cursor-pointer"
                            defaultChecked={displayOptions[key]}
                            onChange={() => {
                              onUpdateConf({
                                ...displayOptions,
                                [key]: !displayOptions[key],
                              });
                            }}
                          />
                          <span className="tw-pl-1">{labels[key]}</span>
                        </label>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          />
        ) : null}

        {entity ? (
          <PublicationSearchResults
            sourceEntity={entity}
            shouldShowAbstract={displayOptions.shouldShowAbstract}
            shouldShowTags={displayOptions.shouldShowTags}
            shouldShowContributions={displayOptions.shouldShowContributions}
          />
        ) : null}
      </div>

      <div className="tw-h-12" />
      <MasterMind />
    </StyledComponentPanel>
  );
}

MainPublications.propTypes = {
  isClinicalResults: PropTypes.bool,
  entities: PropTypes.arrayOf(PropTypes.instanceOf(SourceEntity)),
  portalEntities: PropTypes.arrayOf(PropTypes.instanceOf(SourceEntity)),
};

MainPublications.defaultProps = {
  isClinicalResults: false,
  entities: [],
  portalEntities: [],
};

const mapStateToProps = (state) => {
  return {
    entities: getPublicationCountEntities(state),
  };
};

const Guarded = withErrorGuard(
  React.memo(MainPublications),
  "Publications",
  "Publications"
);

export default connect(mapStateToProps, {})(Guarded);
